import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SntButton from "@/cleanup/widgets/forms/SntButton";

import { useOrg } from "@/contexts/OrgContext";
import * as actions from "@/redux/actions";
import SntOrgMenuSelectorContainer from "@containers/SntOrgSelectorContainer/SntOrgMenuSelectorContainer";

import NavList from "./NavList/NavList";
import useHomeMenu from "./Menus/useHomeMenu";
import useSelectedOrgMenu from "./Menus/useSelectedOrgMenu";
import usePartnerMenu from "./Menus/usePartnerMenu";
import useAllMenu from "./Menus/useAllMenu";

import HomeIcon from "@images/menu/home.svg";
import CogIcon from "@images/menu/cog.svg";
import SensolusLogoIcon from "@images/sensolus/sensolus_logo_white.svg";
import { MenuTypeConst, type MenuRoute } from "./Menus/menuTypes";
import { getHashFromLandingPage } from "@/containers/LandingPageHelper/LandingPageHelper";
import type { RootState } from "@/types/RootState";
import type { LoginInfo } from "@/types/LoginInfo";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasFeature } from "@/hooks/useFeatures";
import { FeatureKey } from "@/types/generated/FeatureKey";
import useUser from "@hooks/useUser";
import useErrorDialog from "@/cleanup/widgets/dialog/useErrorDialog";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import HeaderIconButton from "../Header/HeaderIconButton";

/**
 * Extended menu data with type and loading state
 */
interface MenuData {
  type: string;
  loading: boolean;
  routes: MenuRoute[];
  allPaths: Record<string, MenuRoute>;
  availableMenuPaths: Record<string, MenuRoute>;
  forceMenuPaths?: Record<string, MenuRoute>;
  defaultPath: { pathname: string; search: string } | string;
  isAdminMenuAvailable?: boolean;
}

/**
 * Organisation selector change data
 */
interface OrgSelectorData {
  value: number | null;
}

const SidebarBody = () => {
  const orgContext = useOrg();
  const { orgId, updateOrg } = orgContext ?? { orgId: null, updateOrg: (_newOrgId: number | null) => {} };
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user) as LoginInfo;
  const { whiteLabel } = loginInfo;

  const homeMenu = useHomeMenu(loginInfo) as MenuData;
  const selectedOrgMenu = useSelectedOrgMenu(loginInfo, orgId ?? undefined) as MenuData;
  const partnerMenu = usePartnerMenu(loginInfo) as MenuData;
  const allMenu = useAllMenu(loginInfo) as MenuData;

  const { onImpersonateUser, IMPERSONATE_TYPE, ImpersonateReasonDialogRoot } = useUser();
  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({});

  const [selectedMenu, setSelectedMenu] = useState<MenuData>(homeMenu);

  // The org the menu was last resolved for, used to tell an org-context switch apart
  // from a fresh page load. On a switch we prefer the org default page over a dead-end
  // no_access screen when the current page isn't available in the new org (STIC-15494).
  const prevOrgIdRef = useRef<number | null>(orgId);

  const [lastHomeMenu, setLastHomeMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastAdminMenu, setLastAdminMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastSelelectedOrgMenu, setLastSelectedOrgMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastAllMenu, setLastAllMenu] = useState<{ pathname: string; search: string } | null>(null);

  const [showFooter, setShowFooter] = useState<boolean>(false);
  const { isMobile } = useSelector((state: RootState) => state.all);
  const isSystemUser = loginInfo?.organisation?.organisationType === "SYSTEM";

  const canImpersonate =
    isSystemUser &&
    orgId !== null &&
    orgId !== 0 &&
    (hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY) ||
      hasPermission(UserPermission.ORG_SUPPORT_LOGIN_ADMIN)) &&
    hasFeature(FeatureKey.impersonate);

  const isTargetSystem = orgId === loginInfo.organisation?.id;

  const handleHeaderImpersonate = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (!orgId || isTargetSystem) return;
    let impersonateMode = IMPERSONATE_TYPE.VIEWER;
    if (!hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY)) {
      impersonateMode = IMPERSONATE_TYPE.ADMIN;
    }
    onImpersonateUser({
      id: orgId,
      type: impersonateMode,
      onBadRequest: () => {
        openErrorDialog(translate(LanguageKey.impersonation_insufficient_rights_key));
      },
    });
  };

  const findRouteInAllPaths = (allPaths: Record<string, MenuRoute>, path: string): MenuRoute | null => {
    // First try exact match
    if (allPaths[path]) {
      return allPaths[path];
    }
    // Then try prefix match for paths with dynamic segments (e.g., /site_map_view/514)
    for (const key in allPaths) {
      if (path.startsWith(key + "/")) {
        return allPaths[key];
      }
    }
    return null;
  };

  const getMenuByLocationPath = (path: string): MenuData => {
    // For system users, check allMenu first since dashboards/reports
    // exist in both homeMenu and allMenu
    if (loginInfo?.organisation?.organisationType === "SYSTEM") {
      let routeItem = findRouteInAllPaths(allMenu.allPaths, path);
      if (routeItem) {
        return allMenu;
      }

      routeItem = findRouteInAllPaths(selectedOrgMenu.allPaths, path);
      if (routeItem) {
        return selectedOrgMenu;
      }

      // No homeMenu for system users — fall back to allMenu
      return allMenu;
    }

    let routeItem = findRouteInAllPaths(homeMenu.allPaths, path);
    if (routeItem) {
      return homeMenu;
    }

    routeItem = findRouteInAllPaths(selectedOrgMenu.allPaths, path);
    if (routeItem) {
      return selectedOrgMenu;
    }

    if (loginInfo?.organisation?.organisationType === "PARTNER") {
      routeItem = findRouteInAllPaths(partnerMenu.allPaths, path);
      if (routeItem) {
        return partnerMenu;
      }
    }

    return homeMenu;
  };

  useEffect(() => {
    if (
      homeMenu.loading ||
      selectedOrgMenu.loading ||
      partnerMenu.loading ||
      allMenu.loading
    ) {
      return;
    }

    // Now that every menu has resolved for the current URL org, record whether this
    // evaluation follows an org-context change (the loading guard above means we only
    // get here once the new org's menu is ready, so this is stable across the async
    // menu refetch that a switch triggers).
    const orgChanged = prevOrgIdRef.current !== orgId;
    prevOrgIdRef.current = orgId;

    const menu = getMenuByLocationPath(location.pathname);
    setSelectedMenu(menu);
    dispatch(actions.setMenuType(menu.type));

    // Org-scoped admin routes resolve their menu visibility from the selected
    // org, so they need ?org= in the URL. When a user deep-links to such a
    // top-level route without it (e.g. an external system like Impartner that
    // only knows the route, not the org id), default to the user's own org and
    // let the effect re-run — otherwise the org-dependent menu item is still
    // "not visible" and the access check below would bounce them to the default
    // admin page instead of opening the requested page.
    const ownOrgId = loginInfo.organisation?.id;
    if (menu.type === MenuTypeConst.ADMIN && !orgId && ownOrgId) {
      const adminRoute = selectedOrgMenu.allPaths[location.pathname];
      if (adminRoute && !adminRoute.subPages?.find((p) => p === location.pathname)) {
        updateOrg(ownOrgId);
        return;
      }
    }

    // The user was auto-landed on the (org/user) configured landing page but has no
    // permission to access it. Send them to their first available menu instead of a
    // dead-end /permission or /no_access screen (STIC-15267). This runs before the
    // empty-menu and protected-route checks so it covers a configured landing page in
    // any menu (e.g. ASSET_LIST in the home menu, or TRACKER_ADMIN in the admin menu).
    if (
      location.pathname !== "/" &&
      !location.pathname.includes("/bi_dashboard/") &&
      location.pathname === getHashFromLandingPage(loginInfo)
    ) {
      const landingRoute = menu.allPaths[location.pathname];
      const landingAccessible = !!(
        menu.availableMenuPaths[landingRoute?.to ?? ""] ||
        menu.availableMenuPaths[location.pathname]
      );
      if (!landingAccessible) {
        const orgType = loginInfo?.organisation?.organisationType;
        const primaryMenu =
          orgType === "SYSTEM" ? allMenu : orgType === "PARTNER" ? partnerMenu : homeMenu;
        const fallback =
          typeof primaryMenu.defaultPath === "string"
            ? { pathname: primaryMenu.defaultPath, search: "" }
            : primaryMenu.defaultPath;
        // Guard against a self-loop when the fallback resolves to the same page.
        if (fallback.pathname && fallback.pathname !== location.pathname) {
          history.push(fallback);
          return;
        }
      }
    }

    // empty menu
    if (
      Object.keys(menu.availableMenuPaths).length === 0 &&
      location.pathname !== "/permission"
    ) {
      history.push("/permission");
      return;
    }
    // protected route
    const mainRoute = menu.allPaths[location.pathname];
    if (!mainRoute && location.pathname !== "/") return;
    if (
      location.pathname !== "/no_access" &&
      location.pathname !== "/permission" &&
      location.pathname !== "/"
    ) {
      if (
        (location.pathname.includes("/bi_dashboard/") &&
          !menu.availableMenuPaths["/all_dashboards"]) ||
        (!location.pathname.includes("/bi_dashboard/") &&
          (!mainRoute ||
            !(
              menu.availableMenuPaths[mainRoute.to ?? ""] ||
              menu.availableMenuPaths[location.pathname]
            )))
      ) {
        if (menu.forceMenuPaths && menu.forceMenuPaths[mainRoute.to ?? ""]) {
          const path = menu.defaultPath;
          history.push(`${typeof path === "string" ? path : path.pathname}?org=${orgId}`);
        } else if (orgChanged && orgId) {
          // The page isn't available in the org just switched to (e.g. it lacks the
          // feature/permission that exposed it in the previous org). Land on the org
          // default page instead of a dead-end no_access screen (STIC-15494). A fresh
          // deep-link to an inaccessible page still falls through to no_access below.
          const path = menu.defaultPath;
          const landingPath = typeof path === "string" ? path : path.pathname;
          if (landingPath && landingPath !== location.pathname) {
            history.push(`${landingPath}?org=${orgId}`);
          } else {
            history.push("/no_access");
          }
        } else {
          history.push("/no_access");
        }
        return;
      }
    }

    if (menu.type === MenuTypeConst.ADMIN) {
      // Top-level admin routes without ?org= are defaulted to the user's own
      // org earlier in this effect (and re-run), so reaching here means the
      // org is present (or this is a subpage, which keeps the URL's org).
      setLastAdminMenu(location);
      setLastSelectedOrgMenu(location);
    } else if (menu.type === MenuTypeConst.MAIN) {
      setLastHomeMenu(location);
    } else {
      setLastAdminMenu(location);
      setLastAllMenu(location);
    }

    if (location.pathname === "" || location.pathname === "/") {
      let landingPath = {
        pathname: getHashFromLandingPage(loginInfo),
        search: "",
      };
      // System users land in the system menu by default
      const defaultMenu = loginInfo?.organisation?.organisationType === "SYSTEM" ? allMenu : homeMenu;
      if (!defaultMenu.availableMenuPaths[landingPath.pathname]) {
        landingPath = typeof defaultMenu.defaultPath === "string"
          ? { pathname: defaultMenu.defaultPath, search: "" }
          : defaultMenu.defaultPath;
      }
      history.push(landingPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedOrgMenu, partnerMenu, allMenu, homeMenu]);

  useEffect(() => {
    if (selectedOrgMenu.isAdminMenuAvailable) {
      setShowFooter(true);
      return;
    }

    let isShowFooter = false;
    if (
      hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) &&
      allMenu.isAdminMenuAvailable
    ) {
      isShowFooter = true;
    } else if (
      loginInfo?.organisation?.organisationType === "PARTNER" &&
      partnerMenu.isAdminMenuAvailable
    ) {
      isShowFooter = true;
    }
    setShowFooter(isShowFooter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgMenu, partnerMenu, allMenu]);

  const onOrganisationChanged = (data: OrgSelectorData): void => {
    if (data.value) {
      if (orgId) {
        // Switching between specific orgs/partners: keep the user on the same page in
        // the newly selected org instead of bouncing them to the org landing page
        // (STIC-15494). Resolve entity *detail* subpages (e.g. /trackers_admin_update)
        // to their parent list page — the open record belongs to the previously
        // selected org and would point at the wrong entity under the new org
        // (STIC-15492).
        const parentPath =
          selectedOrgMenu.allPaths[location.pathname]?.to || location.pathname;
        // Only keep the page when it resolves to the org-scoped menu. A page that
        // resolves to the system/All menu for SYSTEM users would leave the sidebar
        // stuck in the wrong menu after the switch, so fall back to the org landing
        // page in that case (STIC-15147).
        const staysInOrgMenu =
          getMenuByLocationPath(parentPath).type === selectedOrgMenu.type;
        if (staysInOrgMenu) {
          history.push(`${parentPath}?org=${data.value}`);
        } else {
          const landingPath =
            typeof selectedOrgMenu.defaultPath === "string"
              ? selectedOrgMenu.defaultPath
              : selectedOrgMenu.defaultPath.pathname;
          history.push(`${landingPath}?org=${data.value}`);
        }
      } else {
        // ALL menu back to selected org Menu: return to the last admin page the user
        // was on, but resolve any entity *detail* subpage (e.g. /trackers_admin_update)
        // to its parent list page (/trackers_admin). The stored record belongs to the
        // previously selected org, so reopening its detail under the newly selected org
        // points at the wrong URL (STIC-15492).
        const path = lastSelelectedOrgMenu || (typeof selectedOrgMenu.defaultPath === "string"
          ? { pathname: selectedOrgMenu.defaultPath, search: "" }
          : selectedOrgMenu.defaultPath);
        const parentPath = selectedOrgMenu.allPaths[path.pathname]?.to || path.pathname;
        history.push(`${parentPath}?org=${data.value}`);
      }
    } else {
      let path: { pathname: string; search: string } | string = "";
      if (loginInfo?.organisation?.organisationType === "PARTNER") {
        path = lastAllMenu || partnerMenu.defaultPath;
      } else {
        path = lastAllMenu || allMenu.defaultPath;
      }
      // Strip the org parameter when switching to "All" mode
      if (typeof path === "object" && path.search) {
        const params = new URLSearchParams(path.search);
        params.delete("org");
        const newSearch = params.toString();
        path = { pathname: path.pathname, search: newSearch ? `?${newSearch}` : "" };
      }
      history.push(path);
    }
  };

  const onAdminButtonClicked = (): void => {
    if (lastAdminMenu) {
      history.push(lastAdminMenu);
    } else {
      const organisationType = loginInfo?.organisation?.organisationType;
      if (organisationType === "NORMAL") {
        history.push(selectedOrgMenu.defaultPath);
      } else if (organisationType === "PARTNER") {
        history.push(partnerMenu.defaultPath);
      } else {
        history.push(allMenu.defaultPath);
      }
    }
  };

  const onHomeMenuClicked = (): void => {
    const path = lastHomeMenu || (typeof homeMenu.defaultPath === "string"
      ? { pathname: homeMenu.defaultPath, search: "" }
      : homeMenu.defaultPath);
    setSelectedMenu(homeMenu);
    history.push(path);
  };

  const isHomeButtonVisible =
    loginInfo?.organisation?.organisationType !== "SYSTEM";

  const showNavIconBar = showFooter && isHomeButtonVisible;

  return (
    <>
      {isSystemUser &&
        !isMobile &&
        document.getElementById("header-org-selector") &&
        createPortal(
          <SntOrgMenuSelectorContainer
            onChange={onOrganisationChanged}
            value={orgId === null ? 0 : orgId}
            variant="pill"
            hideImpersonate
          />,
          document.getElementById("header-org-selector") as HTMLElement
        )}
      {isSystemUser && isMobile && (
        <div className="p-2">
          <SntOrgMenuSelectorContainer
            onChange={onOrganisationChanged}
            value={orgId === null ? 0 : orgId}
            variant="pill"
            hideImpersonate
          />
          {canImpersonate && !isTargetSystem && (
            <SntButton
              onClick={handleHeaderImpersonate}
              className="mt-2"
              title={translate(LanguageKey.support_login_key)}
              style={{ height: 36, padding: "0 12px", fontSize: 14 }}
            >
              <span className="d-inline-flex align-items-center gap-2">
                <SntSupportUserIcon size={18} color="#e67e22" />
                <span>{translate(LanguageKey.support_login_key)}</span>
              </span>
            </SntButton>
          )}
        </div>
      )}
      {loginInfo?.organisation?.organisationType === "PARTNER" &&
        selectedMenu.type !== MenuTypeConst.MAIN && (
          <div className="p-2">
            <SntOrgMenuSelectorContainer
              onChange={onOrganisationChanged}
              value={orgId === null ? 0 : orgId}
            />
          </div>
        )}
      {canImpersonate &&
        !isTargetSystem &&
        document.getElementById("header-support-login") &&
        createPortal(
          <HeaderIconButton
            title={
              isTargetSystem
                ? translate(LanguageKey.support_login_on_system_account_is_not_permitted_key)
                : translate(LanguageKey.support_login_key)
            }
            onClick={handleHeaderImpersonate}
          >
            <SntSupportUserIcon size={20} color="#e67e22" style={{ opacity: isTargetSystem ? 0.4 : 1 }} />
          </HeaderIconButton>,
          document.getElementById("header-support-login") as HTMLElement
        )}
      <div className="sidebar-middle">
        <NavList
          orgId={orgId !== null ? String(orgId) : undefined}
          location={location}
          routes={selectedMenu.routes}
          collapsible={selectedMenu.type === homeMenu.type}
          menuType={selectedMenu.type}
        />
      </div>
      {showNavIconBar || !whiteLabel ? (
        <div
          className={
            "sidebar-footer " +
            (selectedMenu.type === MenuTypeConst.MAIN ? "home-menu" : "admin-menu")
          }
        >
          {showNavIconBar && (
            <div className="nav-icon-bar">
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type === homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onHomeMenuClicked}
                >
                  <img alt="home" src={HomeIcon} />
                  <div>{translate(LanguageKey.home_key)}</div>
                </div>
              </div>
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type !== homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onAdminButtonClicked}
                >
                  <img alt="admin" src={CogIcon} />
                  <div>{translate(LanguageKey.sidebar_admin_key)}</div>
                </div>
              </div>
            </div>
          )}
          {!whiteLabel && (
            <div className="powered-by">
              <div>{translate(LanguageKey.powered_by_key)}</div>
              <img src={SensolusLogoIcon} alt="sensolus" />
            </div>
          )}
        </div>
      ) : (
        ""
      )}
      <ErrorDialog />
      <ImpersonateReasonDialogRoot />
    </>
  );
};

export default SidebarBody;
